import React from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { ArrowLeft, Calendar, CalendarOff, Clock, FileText, LayoutDashboard, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SharedProps, NotaryDetailed } from '@/types'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Badge } from '@/components/ui/badge'

type BreadcrumbEntry = {
  label: string
  href?: string
}

type NotaryPortalLayoutProps = {
  children: React.ReactNode
  title: string
  breadcrumbs?: BreadcrumbEntry[]
}

type NotaryPortalSharedProps = SharedProps & {
  notary_portal: {
    notary: NotaryDetailed
  }
}

const NAV_ITEMS = [
  { href: '/notary_portal/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/notary_portal/profile', icon: User, label: 'Profile' },
  { href: '/notary_portal/availabilities', icon: Clock, label: 'Availability' },
  { href: '/notary_portal/time_offs', icon: CalendarOff, label: 'Time Off' },
  { href: '/notary_portal/appointments', icon: Calendar, label: 'Appointments' },
  { href: '/notary_portal/notarizations', icon: FileText, label: 'Notarizations' },
]

function NotaryPortalNav() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''

  const linkClass = (href: string) =>
    cn(
      "flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[13px] transition-colors",
      pathname.startsWith(href)
        ? "bg-muted font-medium text-foreground"
        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
    )

  return (
    <nav className="px-2 space-y-0.5">
      {NAV_ITEMS.map((item) => (
        <Link key={item.href} href={item.href} className={linkClass(item.href)}>
          <item.icon className="h-4 w-4 shrink-0 opacity-60" />
          {item.label}
        </Link>
      ))}
    </nav>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, string> = {
    approved: 'bg-green-100 text-green-700 border-green-200/60',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200/60',
    rejected: 'bg-red-100 text-red-700 border-red-200/60',
    suspended: 'bg-red-100 text-red-700 border-red-200/60',
  }

  return (
    <Badge className={cn("font-normal text-[10px] capitalize border", config[status] || 'bg-muted text-muted-foreground')}>
      {status}
    </Badge>
  )
}

export function NotaryPortalLayout({ children, title, breadcrumbs = [] }: NotaryPortalLayoutProps) {
  const { flash } = usePage<NotaryPortalSharedProps>().props
  const notary = usePage<NotaryPortalSharedProps>().props.notary_portal?.notary

  const defaultBreadcrumbs: BreadcrumbEntry[] = [
    { label: 'Notary Portal', href: '/notary_portal/dashboard' },
    { label: title },
  ]

  const finalBreadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs

  return (
    <>
      <Head title={`${title} — Notary Portal`} />
      <div className="flex min-h-screen font-display">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-border/40 bg-muted/20">
          <div className="flex h-11 items-center gap-2 px-4">
            <span className="text-[13px] font-semibold text-muted-foreground tracking-wide">Notary Portal</span>
            {notary && <StatusBadge status={notary.status} />}
          </div>

          {notary && (
            <div className="px-4 pb-3 border-b border-border/40 mb-2">
              <p className="text-[13px] font-medium text-foreground truncate">{notary.full_name}</p>
              <p className="text-[11px] text-muted-foreground">
                {notary.commission_state} #{notary.commission_number}
              </p>
            </div>
          )}

          <NotaryPortalNav />

          <div className="border-t border-border/40 mx-2 mt-3 pt-2 pb-2">
            <Link
              href="/accounts/select"
              className="flex items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-[13px] text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 opacity-60" />
              Back to App
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {(flash.notice || flash.alert) && (
            <div className="border-b border-border/40 px-6 py-3">
              {flash.notice && (
                <div className="max-w-2xl mx-auto rounded-lg bg-blue-50 border border-blue-200/60 p-3 text-sm text-blue-700">
                  {flash.notice}
                </div>
              )}
              {flash.alert && (
                <div className="max-w-2xl mx-auto rounded-lg bg-red-50 border border-red-200/60 p-3 text-sm text-red-700">
                  {flash.alert}
                </div>
              )}
            </div>
          )}

          <main className="flex-1 p-6">
            <div className="max-w-2xl mx-auto">
              <Breadcrumb className="mb-4">
                <BreadcrumbList>
                  {finalBreadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {item.href ? (
                          <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{item.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
